// src/components/forums/TopicHeader.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { ForumTopic } from '@/types/forum';
import { Edit, Trash, ThumbsUp } from 'lucide-react';
import { formatRelativeTime } from '@/lib/utils/time';
import {
  toggleTopicLike,
  getTopicLikeCount,
  getTopicLikeStatus,
  getTopicLikeAvatars,
} from '@/lib/supabase/forum';
import ClubBadges from '../shared/ClubBadges';
import { getUserClubBadges } from '@/lib/supabase/clubs-utils';
import type { ClubBadge } from '@/lib/supabase/clubs-utils';
import TopicLikesModal from './TopicLikesModal';

interface TopicHeaderProps {
  topic: ForumTopic;
  onEditClick?: () => void;
  onDeleteClick?: () => void;
}

const TopicHeader: React.FC<TopicHeaderProps> = ({
  topic,
  onEditClick,
  onDeleteClick,
}) => {
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const navigate  = useNavigate();

  const [authorClubs, setAuthorClubs] = useState<ClubBadge[]>([]);
  const [likesCount, setLikesCount]   = useState(0);
  const [isLiked, setIsLiked]         = useState(false);
  const [likeAvatars, setLikeAvatars] = useState<
    {
      auth_id: string;
      full_name: string;
      avatar_url: string | null;
      city?: string | null;
    }[]
  >([]);
  const [isToggling, setIsToggling]   = useState(false);
  const [showLikesModal, setShowLikesModal] = useState(false);

  /* ───────────────────── Author clubs ───────────────────── */
  useEffect(() => {
    if (!topic.author.id) return;
    (async () => {
      try {
        const clubs = await getUserClubBadges(topic.author.id);
        setAuthorClubs(clubs);
      } catch (err) {
        console.error('Error fetching author clubs:', err);
      }
    })();
  }, [topic.author.id]);

  /* ─────── Like count, avatars, and user’s like status ────── */
  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const [count, avatars] = await Promise.all([
          getTopicLikeCount(topic.id),
          getTopicLikeAvatars(topic.id),
        ]);
        if (alive) {
          setLikesCount(count);
          setLikeAvatars(avatars);
        }

        if (alive && user) {
          const liked = await getTopicLikeStatus(topic.id, user.id);
          if (alive) setIsLiked(liked);
        }
      } catch (err) {
        console.error('Error loading topic likes:', err);
      }
    })();

    return () => {
      alive = false;
    };
  }, [topic.id, user?.id]);

  /* ────────────────────── Optimistic like ─────────────────── */
  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user || isToggling) return;

    setIsToggling(true);

    // snapshot
    const prevLiked  = isLiked;
    const nextLiked  = !prevLiked;
    const likeDelta  = prevLiked ? -1 : 1;

    /* 1️⃣ Instant UI update */
    setIsLiked(nextLiked);
    setLikesCount((c) => c + likeDelta);
    setLikeAvatars((prev) => {
      if (nextLiked) {
        const me = {
          auth_id   : user.id,
          full_name : user.full_name ?? user.id,
          avatar_url: user.avatar_url ?? null,
          city      : user.city ?? null,
        };
        return [me, ...prev.filter((a) => a.auth_id !== user.id)];
      }
      return prev.filter((a) => a.auth_id !== user.id);
    });

    /* 2️⃣ Background request */
    toggleTopicLike(topic.id, user.id)
      .then(async ({ liked, count }) => {
        // server wins
        setIsLiked(liked);
        setLikesCount(count);
        const avatars = await getTopicLikeAvatars(topic.id);
        setLikeAvatars(avatars);
      })
      .catch(() => {
        /* 3️⃣ Roll back on failure */
        setIsLiked(prevLiked);
        setLikesCount((c) => c - likeDelta);
        setLikeAvatars((prev) =>
          prevLiked
            ? [
                // restore me
                {
                  auth_id   : user.id,
                  full_name : user.full_name ?? user.id,
                  avatar_url: user.avatar_url ?? null,
                  city      : user.city ?? null,
                },
                ...prev,
              ]
            : prev.filter((a) => a.auth_id !== user.id)
        );
      })
      .finally(() => setIsToggling(false));
  };

  const isAuthor = user?.id === topic.author.id;

  /* ───────────────────────── UI ──────────────────────────── */
  return (
    <div className="bg-dark-200 px-6 pt-6 pb-2 rounded-lg">
      {/* Author */}
      <div className="flex items-center space-x-3 mb-4">
        <img
          src={
            topic.author.image ||
            'https://hebbkx1anhila5yf.public.blob.vercel-storage.com/gold_symbol-removebg-n5agUlyNamyXXN0FU8QGzScjug8DVS.png'
          }
          alt={topic.author.name}
          className="w-12 h-12 rounded-full object-cover border-2 border-dark-400 cursor-pointer"
          onClick={() => navigate(`/dashboard/members/${topic.author.id}`)}
        />

        <div className="flex-1">
          <span
            className="text-white font-medium cursor-pointer hover:text-gold-400 transition-colors"
            onClick={() => navigate(`/dashboard/members/${topic.author.id}`)}
          >
            {topic.author.name}
          </span>

          {authorClubs.length > 0 && (
            <div className="mt-1 mb-1">
              <ClubBadges clubs={authorClubs} size="md" />
            </div>
          )}

          <div
            className={cn(
              'flex items-center text-sm',
              isDarkMode ? 'text-dark-800' : 'text-gray-600'
            )}
          >
            {topic.author.city && (
              <>
                <span>{topic.author.city}</span>
                <span className="mx-2">•</span>
              </>
            )}
            <span>{formatRelativeTime(topic.createdAt)}</span>
          </div>
        </div>
      </div>

      {/* Title + actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start gap-3">
        <h1 className="text-2xl text-white font-semibold flex-1">
          {topic.title}
        </h1>

        <div className="flex items-center space-x-2 ml-0 sm:ml-4">
          {/* Like button (everyone) */}
          <button
            onClick={handleLike}
            className={cn(
              'flex items-center gap-1 px-3 py-1.5 rounded-lg transition-colors',
              isLiked
                ? 'bg-gold-400/20 text-gold-400'
                : 'bg-dark-300 text-dark-800 hover:text-white hover:bg-dark-400',
              isToggling && 'opacity-50 cursor-wait'
            )}
            disabled={isToggling}
          >
            <ThumbsUp className="h-4 w-4" />
            <span>{likesCount}</span>

            {likesCount > 0 && (
              <div
                className="flex -space-x-2 ml-2 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowLikesModal(true);
                }}
              >
                {likeAvatars.slice(0, 4).map((u) => (
                  <img
                    key={u.auth_id}
                    src={
                      u.avatar_url ||
                      'https://hebbkx1anhila5yf.public.blob.vercel-storage.com/gold_symbol-removebg-n5agUlyNamyXXN0FU8QGzScjug8DVS.png'
                    }
                    alt={u.full_name}
                    title={u.full_name}
                    className={cn(
                      'w-5 h-5 rounded-full border object-cover',
                      isDarkMode ? 'border-dark-200' : 'border-white'
                    )}
                  />
                ))}

                {likesCount > 4 && (
                  <div
                    className={cn(
                      'w-5 h-5 rounded-full text-[10px] flex items-center justify-center font-medium',
                      isDarkMode ? 'bg-dark-200 text-white' : 'bg-gray-200 text-gray-700'
                    )}
                  >
                    +{likesCount - 4}
                  </div>
                )}
              </div>
            )}
          </button>

          {/* Edit / delete only for author */}
          {isAuthor && (
            <>
              <button
                onClick={onEditClick}
                className="text-dark-800 hover:text-white"
              >
                <Edit size={20} />
              </button>
              <button
                onClick={onDeleteClick}
                className="text-dark-800 hover:text-red-500"
              >
                <Trash size={20} />
              </button>
            </>
          )}
        </div>
      </div>

      <div className="border-t border-dark-300 mt-2" />

      {/* Likes modal */}
      <TopicLikesModal
        isOpen={showLikesModal}
        onClose={() => setShowLikesModal(false)}
        likeAvatars={likeAvatars}
      />
    </div>
  );
};

export default React.memo(TopicHeader);
